'use client';

import Decimal from 'decimal.js';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { InfoIcon } from '@/lib/components/InfoIcon';
import { NoOffersHint } from '@/lib/components/OrderForm/OrderSummary/NoOffersHint';
import { AnimatedCentText } from '@/lib/components/Text/AnimatedCentText';
import { AnimatedPercentText } from '@/lib/components/Text/AnimatedPercentText';
import { AnimatedUSDText } from '@/lib/components/Text/AnimatedUSDText';
import { CentText } from '@/lib/components/Text/CentText';
import { PercentText } from '@/lib/components/Text/PercentText';
import { USDText } from '@/lib/components/Text/USDText';
import { FEATURE_FLAGS } from '@/lib/config/features';
import { useOrderBookDataById } from '@/lib/hooks/useOrderBookServiceById';
import { useFees } from '@/lib/providers/FeesProvider';
import { useOrderFormStore } from '@/lib/store/order-form-store';
import { Text } from '@/lib/ui-kit/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import {
  calculateAvgMarketBuyPrice,
  calculateMarketBuyShares,
} from '@/lib/utils/calculateAvgMarketPrice';
import { SHARE_DECIMALS, spendableAfterFee } from '@/lib/utils/calculateFees';
import { floorTo } from '@/lib/utils/numbers';

const animated = FEATURE_FLAGS.ENABLE_ORDER_SUMMARY_ANIMATION as boolean;
const UsdValue = animated ? AnimatedUSDText : USDText;
const PercentValue = animated ? AnimatedPercentText : PercentText;
const CentValue = animated ? AnimatedCentText : CentText;

export const MarketBuySummary = () => {
  const t = useTranslations('orderForm');
  const outcomeId = useOrderFormStore.use.selectedOutcomeId();
  const intent = useOrderFormStore.use.intent();
  const amountUsd = useOrderFormStore.use.amountUsd();
  const orderBook = useOrderBookDataById(outcomeId);
  const { fees } = useFees();

  // Check if there are offers in the order book
  const hasOffers = useMemo(() => {
    if (!orderBook) return false;
    const asks = orderBook[intent].asks;
    return asks && asks.length > 0;
  }, [orderBook, intent]);

  // Only what is left after the taker fee buys shares (§ `spendableAfterFee`).
  const spendable = useMemo(
    () => (fees ? spendableAfterFee(amountUsd, fees) : amountUsd),
    [amountUsd, fees],
  );

  const sharesQuantity = useMemo(() => {
    if (amountUsd.eq(0) || !orderBook) {
      return Decimal(0);
    }
    return floorTo(calculateMarketBuyShares(orderBook[intent].asks, spendable), SHARE_DECIMALS);
  }, [amountUsd, spendable, orderBook, intent]);

  const avgPrice = useMemo(() => {
    if (amountUsd.eq(0) || !orderBook) {
      return Decimal(0);
    }
    return calculateAvgMarketBuyPrice(orderBook[intent].asks, spendable);
  }, [amountUsd, spendable, orderBook, intent]);

  // For MARKET orders: payout is shown WITHOUT fees (just shares)

  const pnlPercent = useMemo(() => {
    if (amountUsd.eq(0)) {
      return Decimal(0);
    }
    // For MARKET: all values shown WITHOUT fees
    // Percentage: (Payout - Amount) / Amount
    return sharesQuantity.minus(amountUsd).div(amountUsd);
  }, [amountUsd, sharesQuantity]);

  if (!fees) {
    return null; // Don't render if fees are not loaded yet
  }

  return (
    <div className="flex flex-col gap-1">
      <Text variant="body-small" className="flex w-full items-center justify-between">
        <span className="flex items-center gap-1 text-v2-text-primary opacity-50">
          <Text variant="body-small">
            {hasOffers ? t('payoutIf', { intent }) : t('potentialPayout')}
          </Text>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <InfoIcon />
              </span>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              sideOffset={8}
              className="max-w-[320px] px-2 py-1.5 !text-left"
            >
              <Text variant="label-md">{t('potentialPayoutTooltip')}</Text>
            </TooltipContent>
          </Tooltip>
        </span>
        <span className={hasOffers ? 'title-xs text-v2-green' : 'title-xs text-v2-text-primary'}>
          {hasOffers ? (
            <>
              <UsdValue value={sharesQuantity} alwaysShowDecimals /> (
              <PercentValue value={pnlPercent} decimals={0} />)
            </>
          ) : (
            <NoOffersHint />
          )}
        </span>
      </Text>

      {hasOffers && (
        <Text variant="body-small" className="flex w-full items-center justify-between">
          <span className="flex items-center gap-1 text-v2-text-primary opacity-50">
            <Text variant="body-small">{t('avgPrice')}</Text>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <InfoIcon />
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                sideOffset={8}
                className="max-w-[320px] px-2 py-1.5 !text-left"
              >
                <Text variant="label-md">{t('avgPriceTooltip')}</Text>
              </TooltipContent>
            </Tooltip>
          </span>
          <span className="text-v2-text-primary">
            <CentValue value={avgPrice} decimals={1} />
          </span>
        </Text>
      )}
    </div>
  );
};
